import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import Button from "@cloudscape-design/components/button";
import Icon from "@cloudscape-design/components/icon";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import websocketService from "../services/websocketService";
import { getScenario } from "../services/scenarioApi";
import ScenarioHeader from "./ScenarioHeader";
import PhoneNumbersList from "./PhoneNumbersList";
import ConversationPanel from "./ConversationPanel";
import ToolUsagePanel from "./ToolUsagePanel";
import SessionEndModal from "./SessionEndModal";
import "./ConversationView.css";

function ConversationView({ scenario, sessionId, onEnd }) {
  const { t } = useTranslation();
  
  const [messages, setMessages] = useState([]);
  const [toolInvocations, setToolInvocations] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [error, setError] = useState(null);
  const [showSessionEndModal, setShowSessionEndModal] = useState(false);
  const [sessionStats, setSessionStats] = useState({});
  const [accessDenied, setAccessDenied] = useState(false);
  
  const startTimeRef = useRef(Date.now());
  const lastEventIdRef = useRef(null);
  const onEndRef = useRef(onEnd);
  const sessionIdRef = useRef(sessionId);

  // Keep refs updated
  useEffect(() => { onEndRef.current = onEnd; }, [onEnd]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Verify scenario access on mount
  useEffect(() => {
    if (!scenario?.scenario_id) return;
    getScenario(scenario.scenario_id).catch((err) => {
      if (err.statusCode === 403) {
        setAccessDenied(true);
      }
    });
  }, [scenario?.scenario_id]);

  const handleManualReconnect = useCallback(async () => {
    setConnectionStatus("connecting");
    setError(null);
    try { await websocketService.connect(sessionIdRef.current); } catch { setConnectionStatus("disconnected"); setError(t("conversation.errors.connection_failed")); }
  }, [t]);

  const handleSessionEndAcknowledge = useCallback(() => {
    setShowSessionEndModal(false);
    websocketService.disconnect();
    onEndRef.current?.();
  }, []);

  // Single effect for WebSocket connection - only depends on sessionId
  useEffect(() => {
    let isMounted = true;
    
    const onUserSpeech = (data) => {
      const { eventData } = data;
      setMessages((prev) => {
        if (prev.some((m) => m.event_id === eventData.event_id)) return prev;
        return [...prev, { event_id: eventData.event_id, type: "user_speech", content: eventData.content, timestamp: eventData.timestamp, metadata: eventData.metadata }];
      });
      lastEventIdRef.current = eventData.event_id;
    };

    const onAiResponse = (data) => {
      const { eventData } = data;
      setMessages((prev) => {
        if (prev.some((m) => m.event_id === eventData.event_id)) return prev;
        return [...prev, { event_id: eventData.event_id, type: "ai_response", content: eventData.content, timestamp: eventData.timestamp, metadata: eventData.metadata }];
      });
      lastEventIdRef.current = eventData.event_id;
    };

    const onToolUse = (data) => {
      const { eventData } = data;
      let toolName = eventData.metadata?.tool_name || "Unknown Tool";
      let input = {};
      try {
        if (eventData.content) {
          const parsed = JSON.parse(eventData.content);
          toolName = parsed.tool_name || toolName;
          input = parsed.input || {};
        }
      } catch { /* use defaults */ }
      
      setToolInvocations((prev) => {
        if (prev.some((t) => t.event_id === eventData.event_id)) return prev;
        return [...prev, { event_id: eventData.event_id, tool_name: toolName, input, status: "pending", timestamp: eventData.timestamp }];
      });
      lastEventIdRef.current = eventData.event_id;
    };

    const onToolResult = (data) => {
      const { eventData } = data;
      setToolInvocations((prev) => {
        const toolName = eventData.metadata?.tool_name;
        return prev.map((tool) => {
          if (tool.tool_name === toolName && tool.status === "pending") {
            return { ...tool, output: eventData.metadata?.output || eventData.content, status: eventData.metadata?.error ? "error" : "success", error: eventData.metadata?.error, result_timestamp: eventData.timestamp };
          }
          return tool;
        });
      });
      lastEventIdRef.current = eventData.event_id;
    };

    const onSessionEnd = () => {
      setMessages((currentMessages) => {
        setToolInvocations((currentTools) => {
          const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setSessionStats({ duration, messageCount: currentMessages.length, toolsUsed: currentTools.length });
          setShowSessionEndModal(true);
          return currentTools;
        });
        return currentMessages;
      });
    };

    const onConnectionOpen = () => {
      if (!isMounted) return;
      setConnectionStatus("connected");
      setError(null);
      if (sessionIdRef.current) websocketService.subscribe(sessionIdRef.current);
    };

    const onConnectionClose = () => { if (isMounted) setConnectionStatus("disconnected"); };
    const onConnectionError = () => { if (isMounted) { setConnectionStatus("disconnected"); setError(t("conversation.errors.connection_failed")); } };
    const onReconnecting = () => { if (isMounted) setConnectionStatus("reconnecting"); };
    const onSessionNotFound = () => { if (isMounted) { setConnectionStatus("disconnected"); setError(t("conversation.errors.session_not_found")); } };
    const onMaxReconnectAttempts = () => { if (isMounted) { setConnectionStatus("disconnected"); setError(t("conversation.errors.reconnect_failed")); } };

    websocketService.on("user_speech", onUserSpeech);
    websocketService.on("ai_response", onAiResponse);
    websocketService.on("tool_use", onToolUse);
    websocketService.on("tool_result", onToolResult);
    websocketService.on("session_end", onSessionEnd);
    websocketService.on("connection_open", onConnectionOpen);
    websocketService.on("connection_close", onConnectionClose);
    websocketService.on("connection_error", onConnectionError);
    websocketService.on("reconnecting", onReconnecting);
    websocketService.on("session_not_found", onSessionNotFound);
    websocketService.on("max_reconnect_attempts_reached", onMaxReconnectAttempts);

    websocketService.connect(sessionId).catch(() => { 
      if (isMounted) {
        setConnectionStatus("disconnected"); 
        setError(t("conversation.errors.connection_failed")); 
      }
    });

    return () => {
      isMounted = false;
      websocketService.off("user_speech", onUserSpeech);
      websocketService.off("ai_response", onAiResponse);
      websocketService.off("tool_use", onToolUse);
      websocketService.off("tool_result", onToolResult);
      websocketService.off("session_end", onSessionEnd);
      websocketService.off("connection_open", onConnectionOpen);
      websocketService.off("connection_close", onConnectionClose);
      websocketService.off("connection_error", onConnectionError);
      websocketService.off("reconnecting", onReconnecting);
      websocketService.off("session_not_found", onSessionNotFound);
      websocketService.off("max_reconnect_attempts_reached", onMaxReconnectAttempts);
      websocketService.disconnect();
    };
  }, [sessionId, t]);

  const getStatusDisplay = () => {
    const statuses = {
      connected: { text: t("conversation.status.connected"), className: "connected", icon: "status-positive" },
      connecting: { text: t("conversation.status.connecting"), className: "connecting", icon: "status-in-progress" },
      reconnecting: { text: t("conversation.status.reconnecting"), className: "reconnecting", icon: "status-in-progress" },
      disconnected: { text: t("conversation.status.disconnected"), className: "disconnected", icon: "status-negative" },
    };
    return statuses[connectionStatus] || statuses.disconnected;
  };

  const statusDisplay = getStatusDisplay();

  if (accessDenied) {
    return (
      <main className="conversation-view">
        <div className="conversation-view-header">
          <Button variant="link" iconName="arrow-left" onClick={() => onEnd?.()}>
            {t("conversation.return_to_scenarios")}
          </Button>
        </div>
        <Alert
          type="error"
          action={
            <Button variant="primary" onClick={() => onEnd?.()}>
              {t("conversation.return_to_scenarios")}
            </Button>
          }
        >
          {t("errors.api.forbidden")}
        </Alert>
      </main>
    );
  }

  return (
    <main className="conversation-view">
      <div className="conversation-view-header">
        <Button variant="link" iconName="arrow-left" onClick={() => onEnd?.()}>
          {t("conversation.return_to_scenarios")}
        </Button>
      </div>
      
      <ScenarioHeader scenario={scenario} />

      {scenario?.call_direction !== "outbound" && <PhoneNumbersList />}
      
      <div className={`connection-status ${statusDisplay.className}`} role="status" aria-live="polite">
        <Icon name={statusDisplay.icon} />
        <span>{statusDisplay.text}</span>
      </div>

      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}
          action={connectionStatus === "disconnected" && (
            <SpaceBetween direction="horizontal" size="xs">
              {error !== t("conversation.errors.session_not_found") && <Button onClick={handleManualReconnect}>{t("conversation.errors.reconnect")}</Button>}
              <Button variant="primary" onClick={() => onEnd?.()}>{t("conversation.session_end.acknowledge")}</Button>
            </SpaceBetween>
          )}>
          {error}
        </Alert>
      )}

      <div className="conversation-split-panel">
        <ConversationPanel messages={messages} />
        <ToolUsagePanel toolInvocations={toolInvocations} />
      </div>

      <SessionEndModal visible={showSessionEndModal} stats={sessionStats} onAcknowledge={handleSessionEndAcknowledge} />
    </main>
  );
}

export default ConversationView;
